import { Form } from 'antd';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { FormContext } from './interface';

/**
 * Custom hook for managing form input values with focus/blur handling
 * and Ant Design Form integration.
 *
 * @param {Object} params - Configuration object
 * @param {any} [params.defaultValue] - Initial default value
 * @param {any} [params.value] - Controlled value
 * @param {string} [params.id] - Field identifier
 * @param {Function} [params.onFocus] - Focus event callback
 * @param {Function} [params.onBlur] - Blur event callback
 * @returns {Object} Helper methods and state
 */
export function useValueHandle({
  defaultValue,
  value,
  id,
  onFocus,
  onBlur,
}: {
  defaultValue?: any;
  value?: any;
  id?: string;
  onFocus?: (...args: any) => void;
  onBlur?: (...args: any) => void;
}) {
  // Track initial render to avoid immediate value overwrite
  const initFlag = useRef(false);
  const [isFocus, setIsFocus] = useState(false);
  const { form, name: formName } = useContext(FormContext);
  const [inputValue, setInputValue] = useState(defaultValue ?? value);

  // Watch for form value changes (Ant Design Form integration)
  const changeValue = Form.useWatch(
    formName ? id?.replace(formName + '_', '') : id,
    form
  );

  /**
   * Handles focus events and updates focus state
   * @param {...any} args - Original focus event arguments
   */
  const handleFocus = useCallback(
    (...args: any) => {
      setIsFocus(true);
      if (typeof onFocus === 'function') {
        onFocus(...args);
      }
    },
    [onFocus]
  );

  /**
   * Handles blur events and updates focus state
   * @param {...any} args - Original blur event arguments
   */
  const handleBlur = useCallback(
    (...args: any) => {
      setIsFocus(false);
      if (typeof onBlur === 'function') {
        onBlur(args);
      }
    },
    [onBlur]
  );

  // Sync with external value changes (controlled component pattern)
  useEffect(() => {
    if (initFlag.current) {
      setInputValue(value);
    }
    initFlag.current = true;
  }, [value]);

  // Sync with Ant Design Form value changes
  useEffect(() => {
    if (form && id) {
      setInputValue(changeValue);
    }
  }, [changeValue, form]);

  return {
    /**
     * Determines if the field has a value
     * - Arrays: has length > 0
     * - Numbers: always true (including 0)
     * - Other: truthy check
     */
    hasValue: Array.isArray(inputValue)
      ? inputValue.length > 0
      : typeof value === 'number'
        ? true
        : !!inputValue,
    handleChange: setInputValue,
    handleFocus,
    handleBlur,
    isFocus,
  };
}
